import { useState, useEffect } from "react";
import { useQuery, useMutation } from "@tanstack/react-query";
import { supabase } from "@/shared/lib/supabase";
import * as DialogPrimitive from "@radix-ui/react-dialog";
import { Button } from "@/shared/components/ui/button";
import { Input } from "@/shared/components/ui/input";
import { FieldError } from "@/shared/components/ui/FieldError";
import { Check, Loader2, X } from "lucide-react";
import { IconButton } from "@/shared/components/ui/icon-button";

// Type for user data from search query
interface UserSearchResult {
  user_id: number;
  email: string;
  first_name: string | null;
  last_name: string | null;
  full_name: string | null;
  customizations?: {
    lawyer_application?: {
      firm_name?: string;
      bar_number?: string;
    };
  };
}

interface AddLawyerDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  onSuccess: () => void;
}

export function AddLawyerDialog({ open, onOpenChange, onSuccess }: AddLawyerDialogProps) {
  const [searchInput, setSearchInput] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [selectedUser, setSelectedUser] = useState<UserSearchResult | null>(null);
  const [firmName, setFirmName] = useState("");
  const [barNumber, setBarNumber] = useState("");
  const [error, setError] = useState("");

  // Debounce search input
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(searchInput);
    }, 300);

    return () => clearTimeout(timer);
  }, [searchInput]);

  // Search for users with autosearch
  const { data: users, isLoading: searchLoading } = useQuery({
    queryKey: ["search-users-for-lawyer", debouncedSearch],
    queryFn: async () => {
      if (!debouncedSearch.trim()) return [];

      const search = `%${debouncedSearch.trim()}%`;

      const { data, error } = await supabase
        .from("users")
        .select("user_id, email, first_name, last_name, full_name")
        .or(`email.ilike.${search},full_name.ilike.${search}`)
        .limit(10);

      if (error) throw error;

      // Filter out users who are already lawyers
      const { data: existingLawyers } = await supabase.from("lawyers").select("user_id");

      const lawyerUserIds = new Set(existingLawyers?.map((l) => l.user_id) || []);

      return (data || []).filter((user) => !lawyerUserIds.has(user.user_id));
    },
    enabled: debouncedSearch.length >= 2,
  });

  // Mutation to add lawyer
  const addLawyerMutation = useMutation({
    mutationFn: async () => {
      if (!selectedUser) throw new Error("No user selected");

      const { error } = await supabase.from("lawyers").insert({
        user_id: selectedUser.user_id,
        firm_name: firmName || null,
        bar_number: barNumber || null,
      });

      if (error) throw error;
    },
    onSuccess: () => {
      resetForm();
      onSuccess();
    },
    onError: (err: Error) => {
      setError(err.message || "Failed to add lawyer");
    },
  });

  const resetForm = () => {
    setSearchInput("");
    setDebouncedSearch("");
    setSelectedUser(null);
    setFirmName("");
    setBarNumber("");
    setError("");
  };

  const handleSelectUser = (user: UserSearchResult) => {
    setSelectedUser(user);
    // Pre-fill firm name and bar number from customizations if available
    if (user.customizations?.lawyer_application) {
      setFirmName(user.customizations.lawyer_application.firm_name || "");
      setBarNumber(user.customizations.lawyer_application.bar_number || "");
    }
  };

  const handleSubmit = () => {
    if (!selectedUser) {
      setError("Please select a user");
      return;
    }
    addLawyerMutation.mutate();
  };

  const handleClose = () => {
    resetForm();
    onOpenChange(false);
  };

  const showResults = debouncedSearch.length >= 2;

  return (
    <DialogPrimitive.Root open={open} onOpenChange={handleClose}>
      <DialogPrimitive.Portal>
        <DialogPrimitive.Overlay className="fixed inset-0 z-50 bg-black/50" />
        <DialogPrimitive.Content className="fixed left-1/2 top-1/2 z-50 -translate-x-1/2 -translate-y-1/2 bg-white rounded-lg shadow-lg w-full max-w-[425px] focus:outline-none">
          <div className="p-6">
            {/* Close button */}
            <DialogPrimitive.Close asChild>
              <IconButton className="absolute right-4 top-4" label="Close" tooltip={false}>
                <X />
              </IconButton>
            </DialogPrimitive.Close>

            {/* Header */}
            <h2 className="text-xl font-medium text-stone-900">Add Lawyer</h2>
            <p className="text-sm text-stone-500 mb-6">Search for a user to grant lawyer access</p>

            {/* Search Input */}
            <div className="space-y-2 mb-4">
              <label htmlFor="user-search" className="text-sm text-stone-700">
                Search by name or email
              </label>
              <Input
                id="user-search"
                placeholder="Start typing to search..."
                value={searchInput}
                onChange={(e) => {
                  setSearchInput(e.target.value);
                  setSelectedUser(null);
                }}
              />
            </div>

            {/* Loading */}
            {searchLoading && (
              <div className="flex items-center justify-center py-4">
                <Loader2 className="h-5 w-5 animate-spin text-stone-400" />
              </div>
            )}

            {/* Search Results */}
            {showResults && !searchLoading && users && users.length > 0 && (
              <div className="border rounded-md divide-y max-h-[180px] overflow-y-auto mb-4">
                {users.map((user) => (
                  <div
                    key={user.user_id}
                    className={`px-3 py-2.5 flex items-center justify-between cursor-pointer transition-colors ${
                      selectedUser?.user_id === user.user_id ? "bg-stone-100" : "hover:bg-stone-50"
                    }`}
                    onClick={() => handleSelectUser(user)}
                  >
                    <div>
                      <p className="text-sm font-medium text-stone-900">
                        {user.full_name ||
                          `${user.first_name || ""} ${user.last_name || ""}`.trim() ||
                          "Unknown"}
                      </p>
                      <p className="text-sm text-stone-500 mt-0.5">{user.email}</p>
                    </div>
                    {selectedUser?.user_id === user.user_id && (
                      <Check className="h-4 w-4 text-stone-600" />
                    )}
                  </div>
                ))}
              </div>
            )}

            {/* No results */}
            {showResults && !searchLoading && (!users || users.length === 0) && (
              <p className="text-sm text-stone-500 text-center py-3 mb-4">No users found</p>
            )}

            {/* Error */}
            {error && <FieldError message={error} className="mb-4" />}

            {/* Actions */}
            <div className="flex justify-end gap-3">
              <Button variant="outline" onClick={handleClose}>
                Cancel
              </Button>
              <Button
                onClick={handleSubmit}
                disabled={!selectedUser || addLawyerMutation.isPending}
                className="bg-teal-600 hover:bg-teal-700 disabled:bg-stone-200 disabled:text-stone-400"
              >
                {addLawyerMutation.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                Add Lawyer
              </Button>
            </div>
          </div>
        </DialogPrimitive.Content>
      </DialogPrimitive.Portal>
    </DialogPrimitive.Root>
  );
}
